import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  useConversation,
  useMessages,
  useSendMessage,
  useTypingIndicator,
  useTypingStatus,
  useBlockUser,
  useReport,
} from '@/hooks/useChat';
import {
  ChatHeader,
  MessageThread,
  MessageInput,
  AIChatInline,
} from '@/components/chat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

export default function Chat() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [showAIChat, setShowAIChat] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Queries
  const { data: conversation, isLoading: isLoadingConversation } = useConversation(matchId);
  const { data: messagesData } = useMessages(
    conversation?.id
  );
  const { data: typingData } = useTypingStatus(conversation?.id);

  // Mutations
  const sendMessage = useSendMessage();
  const typingIndicator = useTypingIndicator(conversation?.id);
  const blockUser = useBlockUser();
  const report = useReport();

  // Get current user's internal ID
  useEffect(() => {
    if (conversation && user) {
      // The current user is whichever one matches the other user
      const otherId = conversation.otherUser?.id;
      if (otherId) {
        // We need to determine current user ID from conversation
        // It's the one that's NOT the other user
        const u1 = (conversation as any).user1Id;
        const u2 = (conversation as any).user2Id;
        setCurrentUserId(u1 === otherId ? u2 : u1);
      }
    }
  }, [conversation, user]);

  const handleSend = (content: string) => {
    if (!conversation?.id || !currentUserId) return;

    sendMessage.mutate({
      conversationId: conversation.id,
      content,
      currentUserId,
    });
  };

  const handleTyping = (isTyping: boolean) => {
    typingIndicator.mutate(isTyping);
  };

  const handleAiSuggest = () => {
    setShowAIChat(true);
  };

  const handleBlock = async () => {
    if (!conversation?.otherUser?.id) return;

    try {
      await blockUser.mutateAsync({ userId: conversation.otherUser.id });
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  };

  const handleReport = async () => {
    if (!conversation?.otherUser?.id) return;

    try {
      await report.mutateAsync({
        reportedId: conversation.otherUser.id,
        conversationId: conversation.id,
        category: 'safety_concern',
      });
      setShowReportDialog(false);
    } catch (error) {
      console.error('Failed to report user:', error);
    }
  };

  // Loading state
  if (isLoadingConversation) {
    return (
      <div className="h-screen flex items-center justify-center bg-brand-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  // Error state
  if (!conversation) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-4 bg-brand-background">
        <p className="text-brand-muted mb-4">Conversation not found</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-brand-primary hover:underline"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const otherUser = conversation.otherUser;
  const primaryPhoto = otherUser?.photos?.[0]?.url;

  return (
    <div className="h-screen flex flex-col bg-brand-background text-brand-text">
      <ChatHeader
        name={otherUser?.displayName || 'Unknown'}
        photo={primaryPhoto}
        onBlock={() => setShowBlockDialog(true)}
        onReport={() => setShowReportDialog(true)}
      />

      {/* Message thread - shrinks when AI panel is open */}
      <MessageThread
        messages={messagesData?.messages || []}
        currentUserId={currentUserId || ''}
        isTyping={typingData?.isTyping}
        otherUserName={otherUser?.displayName}
      />

      {/* Bottom panel - HUD-style with glowing neon divider */}
      {showAIChat && matchId && otherUser ? (
        <div className="h-[50vh] md:h-[45vh] relative bg-gradient-to-t from-brand-surface/95 to-brand-background/90 backdrop-blur-sm flex flex-col md:flex-row animate-in slide-in-from-bottom duration-300">
          {/* Top glow line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary to-transparent opacity-60" />
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-primary to-transparent blur-sm opacity-40" />

          {/* Left: AI Coach */}
          <div className="flex-1 flex flex-col min-h-0 relative">
            <AIChatInline
              matchProfile={{
                id: otherUser.id,
                displayName: otherUser.displayName || 'Unknown',
                photos: otherUser.photos,
              }}
              matchId={matchId}
              onClose={() => setShowAIChat(false)}
            />
          </div>

          {/* Neon divider - vertical on desktop, horizontal on mobile */}
          <div className="hidden md:block relative w-px self-stretch my-4">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-primary to-transparent opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-primary to-transparent blur-md opacity-50" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-brand-primary rounded-full shadow-[0_0_10px_2px] shadow-brand-primary/60" />
          </div>
          <div className="md:hidden relative h-px mx-4">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-primary to-transparent opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-primary to-transparent blur-md opacity-50" />
          </div>

          {/* Right: Your chat input */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-4 py-3 flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-brand-primary rounded-full shadow-[0_0_6px_1px] shadow-brand-primary/60" />
              <span className="text-sm font-medium text-white/80">Your Reply</span>
            </div>
            <div className="flex-1 overflow-hidden" />
            <MessageInput
              onSend={handleSend}
              onTyping={handleTyping}
              onAiSuggest={() => setShowAIChat(false)}
              isSending={sendMessage.isPending}
              isAiLoading={false}
            />
          </div>
        </div>
      ) : (
        <MessageInput
          onSend={handleSend}
          onTyping={handleTyping}
          onAiSuggest={handleAiSuggest}
          isSending={sendMessage.isPending}
          isAiLoading={false}
        />
      )}

      {/* Block Confirmation Dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block {otherUser?.displayName}?</AlertDialogTitle>
            <AlertDialogDescription>
              They won't be able to message you or see your profile. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlock}
              className="bg-red-600 hover:bg-red-700"
            >
              Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Confirmation Dialog */}
      <AlertDialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Report {otherUser?.displayName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Our team will review this report. You can also block this user if
              you feel unsafe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReport}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
